(() => {
  if (window.EWVariantSequencing) return;

  const MATRIX_SELECTOR = '[data-ew-variant-sequencing-matrix]';
  const GROUP_SELECTOR = 'fieldset, .product-form__input--dropdown';
  const controllers = new WeakMap();

  function parseMatrix(script) {
    try {
      const matrix = JSON.parse(script.textContent);
      if (!Array.isArray(matrix.optionNames) || !Array.isArray(matrix.variants)) return null;
      if (!matrix.variants.every((variant) => Array.isArray(variant.options))) return null;
      return matrix;
    } catch (error) {
      console.error('EW variant sequencing matrix could not be parsed.', error);
      return null;
    }
  }

  function selectedValue(group) {
    const select = group.querySelector('select');
    if (select) return select.value;
    return group.querySelector('input:checked')?.value ?? null;
  }

  function choices(group) {
    const select = group.querySelector('select');
    if (select) return Array.from(select.options);
    return Array.from(group.querySelectorAll('input[type="radio"]'));
  }

  function setSelected(group, value) {
    const select = group.querySelector('select');
    if (select) {
      select.value = value;
      Array.from(select.options).forEach((option) => {
        option.toggleAttribute('selected', option.value === value);
      });
      return;
    }

    group.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = input.value === value;
    });
  }

  function setChoicePossible(choice, possible) {
    if (choice.tagName === 'OPTION') {
      if (!possible) {
        choice.dataset.ewSequenceImpossible = 'true';
        choice.hidden = true;
        choice.disabled = true;
      } else if (choice.dataset.ewSequenceImpossible === 'true') {
        delete choice.dataset.ewSequenceImpossible;
        choice.hidden = false;
        choice.disabled = false;
      }
      return;
    }

    const label = choice.id ? document.querySelector(`label[for="${CSS.escape(choice.id)}"]`) : null;
    if (!possible) {
      choice.dataset.ewSequenceImpossible = 'true';
      choice.disabled = true;
      choice.hidden = true;
      choice.setAttribute('aria-hidden', 'true');
      if (label) label.hidden = true;
    } else if (choice.dataset.ewSequenceImpossible === 'true') {
      delete choice.dataset.ewSequenceImpossible;
      choice.disabled = false;
      choice.hidden = false;
      choice.removeAttribute('aria-hidden');
      if (label) label.hidden = false;
    }
  }

  class EWVariantSequencingController {
    constructor(productInfo, matrix) {
      this.productInfo = productInfo;
      this.matrix = matrix;
      this.handleChangeCapture = this.handleChangeCapture.bind(this);
      productInfo.addEventListener('change', this.handleChangeCapture, true);
      this.apply();
    }

    handleChangeCapture(event) {
      const variantSelects = event.target.closest('variant-selects');
      if (!variantSelects || !this.productInfo.contains(variantSelects)) return;
      this.apply(variantSelects);
    }

    apply(variantSelects = this.productInfo.querySelector('variant-selects')) {
      if (!variantSelects) return;
      const groups = Array.from(variantSelects.querySelectorAll(GROUP_SELECTOR));
      if (groups.length !== this.matrix.optionNames.length) return;

      const precedingSelections = [];
      groups.forEach((group, optionIndex) => {
        const validValues = new Set(
          this.matrix.variants
            .filter((variant) =>
              precedingSelections.every((selection, index) => variant.options[index] === selection)
            )
            .map((variant) => variant.options[optionIndex])
        );

        const groupChoices = choices(group);
        groupChoices.forEach((choice) => setChoicePossible(choice, validValues.has(choice.value)));

        let currentValue = selectedValue(group);
        if (!validValues.has(currentValue)) {
          const firstValidChoice = groupChoices.find((choice) => validValues.has(choice.value));
          if (firstValidChoice) {
            currentValue = firstValidChoice.value;
            setSelected(group, currentValue);
          }
        }
        precedingSelections.push(currentValue);
      });
    }
  }

  function initialize(root = document) {
    root.querySelectorAll(MATRIX_SELECTOR).forEach((script) => {
      const productInfo = script.closest('product-info');
      if (!productInfo || controllers.has(productInfo)) return;
      const matrix = parseMatrix(script);
      if (!matrix) return;
      controllers.set(productInfo, new EWVariantSequencingController(productInfo, matrix));
    });
  }

  window.EWVariantSequencing = { initialize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      initialize();
      document.querySelectorAll(MATRIX_SELECTOR).forEach((script) => {
        controllers.get(script.closest('product-info'))?.apply();
      });
    });
  }
})();
